import React, { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { db } from "@lib/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Button } from "@mmfestival/shared-components";

const formatDate = (dates) => {
  if (!dates || dates.length === 0) return "Date unavailable";
  const valid = dates
    .filter((d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d))
    .map((d) => new Date(d))
    .filter((d) => !isNaN(d));
  if (valid.length === 0) return "Date unavailable";

  valid.sort((a, b) => a - b);

  const fmt = valid.map((d) => ({
    day: d.toLocaleDateString("en-US", { day: "numeric" }),
    month: d.toLocaleDateString("en-US", { month: "long" }),
    year: d.toLocaleDateString("en-US", { year: "numeric" }),
  }));

  const uniq = [];
  const seen = new Set();
  fmt.forEach((d) => {
    const s = `${d.day} ${d.month} ${d.year}`;
    if (!seen.has(s)) {
      seen.add(s);
      uniq.push(d);
    }
  });

  return uniq.length === 1
    ? `${uniq[0].day} ${uniq[0].month} ${uniq[0].year}`
    : `${uniq[0].day} - ${uniq[uniq.length - 1].day} ${
        uniq[uniq.length - 1].month
      } ${uniq[uniq.length - 1].year}`;
};

// Normalize various possible status strings (ID/EN variants) into English labels:
// "Registration Open", "Coming Soon", "Registration Closed", plus other canonical EN statuses.
const translateStatus = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  const sl = s.toLowerCase();

  // canonical quick matches (ID or EN)
  const canonical = {
    "registrasi dibuka": "Registration Open",
    "pendaftaran dibuka": "Registration Open",
    "registrasi dibuka.": "Registration Open",
    "pendaftaran dibuka.": "Registration Open",
    dibuka: "Registration Open",
    "registration open": "Registration Open",
    open: "Registration Open",

    "registrasi ditutup": "Registration Closed",
    "pendaftaran ditutup": "Registration Closed",
    ditutup: "Registration Closed",
    "registration closed": "Registration Closed",
    closed: "Registration Closed",

    "segera dibuka": "Coming Soon",
    segera: "Coming Soon",
    "coming soon": "Coming Soon",

    "sedang berlangsung": "Ongoing",
    berlangsung: "Ongoing",
    ongoing: "Ongoing",

    selesai: "Finished",
    "selesai.": "Finished",
    finished: "Finished",
    completed: "Finished",

    dibatalkan: "Cancelled",
    batal: "Cancelled",
    cancelled: "Cancelled",
    canceled: "Cancelled",
  };

  if (canonical[sl]) return canonical[sl];

  // heuristics / contains-based
  if (sl.includes("segera") || sl.includes("coming") || sl.includes("soon"))
    return "Coming Soon";
  if (
    sl.includes("dibuka") ||
    (sl.includes("pendaftaran") && sl.includes("buka")) ||
    sl.includes("open")
  )
    return "Registration Open";
  if (sl.includes("ditutup") || sl.includes("tutup") || sl.includes("closed"))
    return "Registration Closed";
  if (sl.includes("berlangsung") || sl.includes("ongo")) return "Ongoing";
  if (sl.includes("selesai") || sl.includes("done") || sl.includes("completed"))
    return "Finished";
  if (sl.includes("batal") || sl.includes("cancel")) return "Cancelled";

  // fallback: try to return a readable English-like string (title case)
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const EventCard = React.memo(({ event, index }) => {
  const isOpen = event.status === "Registration Open";

  return (
    <div className="bg-white border border-slate-200 flex flex-col text-left relative overflow-hidden transition-all duration-300 hover:border-slate-300 rounded-2xl shadow-md hover:shadow-lg">
      <div className="relative w-full h-60 overflow-hidden group rounded-t-2xl">
        <img
          src={event.image || "https://via.placeholder.com/800x400"}
          alt={event.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 via-gray-600/50 to-gray-600/20 transition-opacity duration-300 group-hover:from-gray-600/100 group-hover:via-gray-600/60 group-hover:to-gray-600/30" />
        {event.logo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <img
              src={event.logo}
              alt={`${event.title} Logo`}
              className="h-32 md:h-30 w-auto object-contain drop-shadow-2xl"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}
      </div>

      <div className="px-4 py-4 flex flex-col flex-grow text-slate-700">
        <motion.h3
          className="text-lg font-semibold mt-2 text-slate-900 uppercase"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          {event.title}
        </motion.h3>

        <motion.p
          className="text-sm uppercase text-slate-500 mt-1"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.05 }}
          viewport={{ once: true }}
        >
          {event.location || "Location unavailable"}
        </motion.p>

        <motion.p
          className="text-md text-slate-600 mt-3 line-clamp-3"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.1 }}
          viewport={{ once: true }}
        >
          {event.aboutEvent || "Description unavailable"}
        </motion.p>

        <ul className="text-left mt-3 space-y-2 text-sm">
          <motion.li
            className="flex items-center text-slate-600"
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.15 }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-2.5 text-slate-400 w-4"
            />
            <span>
              {event.displayDate ||
                formatDate(
                  event.categories &&
                    event.categories.map((c) => (c && c.date ? c.date : null))
                )}
            </span>
          </motion.li>

          <motion.li
            className="flex items-center text-slate-600"
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
            viewport={{ once: true }}
          >
            <div
              className={`w-2 h-2 rounded-full mr-2.5 flex-shrink-0 ${
                event.status === "Registration Open"
                  ? "bg-green-600"
                  : event.status === "Registration Closed"
                  ? "bg-rose-500"
                  : "bg-amber-500"
              }`}
            />
            <span className="text-sm font-medium">
              {event.status || "Loading..."}
            </span>
          </motion.li>
        </ul>

        <motion.div
          className="mt-4 pt-3 border-t border-slate-100"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.25 }}
          viewport={{ once: true }}
        >
          {isOpen || event.status === "Registration Closed" ? (
            <Link to={event.detailPath} className="block">
              <Button fullWidth className="!bg-amber-400 !text-slate-900 !hover:bg-amber-500 hover:shadow-lg group">
                DISCOVER EVENT
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                />
              </Button>
            </Link>
          ) : (
            // When status is "Coming Soon" or others (except Closed/Open), render a disabled button (no Link)
            <Button
              fullWidth
              disabled
              className="bg-slate-100 text-slate-400"
              title={
                event.status
                  ? `Not available — status: ${event.status}`
                  : "Discover disabled — status unavailable"
              }
            >
              DISCOVER EVENT
              <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
            </Button>
          )}
        </motion.div>
      </div>
    </div>
  );
});

export const SectionTrilogy = React.memo(() => {
  // Reordered so leftmost card is BMM, then JMM, then SMM
  const hardcodedEvents = [
    {
      id: "BMM",
      title: "BALI MOUNTAIN MARATHON",
      image: "/bg/bali.jpg",
      logo: "/logo/BMM.png",
      location: "Bedugul, Bali - Indonesia",
      slug: "BMM",
      detailPath: "/BMM",
    },
    {
      id: "JMM",
      title: "JAVA MOUNTAIN MARATHON",
      image: "/bg/java.jpg",
      logo: "/logo/JMM.png",
      location: "Patuha, West Java - Indonesia",
      slug: "JMM",
      detailPath: "/JMM",
    },
    {
      id: "SMM",
      title: "SEMBALUN MOUNTAIN MARATHON",
      image: "/bg/lombok.jpg",
      logo: "/logo/SMM.png",
      location: "Sembalun, Lombok - Indonesia",
      slug: "SMM",
      detailPath: "/SMM",
    },
  ];

  const [events, setEvents] = useState(
    hardcodedEvents.map((h) => ({
      ...h,
      status: null,
      aboutEvent: null,
      categories: [],
    }))
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchFields = async () => {
      try {
        const ids = hardcodedEvents.map((e) => e.id);
        const q = query(
          collection(db, "semuaKegiatan"),
          where("__name__", "in", ids)
        );
        const snap = await getDocs(q);
        const remoteMap = {};
        snap.forEach((doc) => {
          const d = doc.data() || {};
          // translate status to canonical English labels
          const translated = translateStatus(d.status ?? d.statusText ?? "");
          remoteMap[doc.id] = {
            status: translated ?? null,
            aboutEvent: d.aboutEvent ?? null,
            categories: Array.isArray(d.categories)
              ? d.categories
              : d.categories
              ? [d.categories]
              : [],
          };
        });

        const merged = hardcodedEvents.map((h) => {
          const r = remoteMap[h.id] || {};
          return {
            ...h,
            status: r.status ?? null,
            aboutEvent: r.aboutEvent ?? null,
            categories: r.categories ?? [],
          };
        });

        if (mounted) {
          setEvents(merged);
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching preview fields from Firestore:", err);
        if (mounted) {
          setError("Failed to load event data from server");
        }
      }
    };

    fetchFields();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedActivities = useMemo(() => {
    // Desired display order (left to right): BMM, JMM, SMM
    const displayOrder = ["BMM", "JMM", "SMM"];

    const mapped = events.map((data) => {
      const datesFromCategories =
        data.categories && Array.isArray(data.categories)
          ? [
              ...new Set(
                data.categories.map((c) => (c && c.date ? c.date : ""))
              ),
            ].filter(Boolean)
          : [];

      // Override map: force specific displayed dates regardless of Firestore content
      const overrideDatesMap = {
        BMM: ["2026-01-10", "2026-01-11"], // 10-11 January 2026
        JMM: ["2026-04-25", "2026-04-26"], // 25-26 April 2026
        SMM: ["2026-09-19", "2026-09-20"], // 19-20 September 2026
      };

      let displayDate = null;
      if (Object.prototype.hasOwnProperty.call(overrideDatesMap, data.id)) {
        if (overrideDatesMap[data.id] === null) {
          displayDate = "TBA";
        } else {
          displayDate = formatDate(overrideDatesMap[data.id]);
        }
      } else {
        displayDate = formatDate(datesFromCategories);
      }

      return {
        id: data.id,
        title: data.title || "Title unavailable",
        image: data.image || "",
        logo: data.logo || null,
        location: data.location || "Location unavailable",
        status: data.status || null,
        slug: data.slug || "",
        dates: datesFromCategories,
        description: data.aboutEvent || null,
        aboutEvent: data.aboutEvent || null,
        categories: data.categories || [],
        detailPath: data.detailPath || `/detail/${data.slug}`,
        // new property used by EventCard to display date override / custom string
        displayDate,
      };
    });

    // Sort using displayOrder priority; fall back to earliest date if not in the list
    mapped.sort((a, b) => {
      const ia = displayOrder.indexOf(a.id);
      const ib = displayOrder.indexOf(b.id);
      if (ia !== -1 || ib !== -1) {
        const vala = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
        const valb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
        return vala - valb;
      }
      const dateA = a.dates[0] ? new Date(a.dates[0]) : new Date(0);
      const dateB = b.dates[0] ? new Date(b.dates[0]) : new Date(0);
      return dateA - dateB;
    });

    return mapped.slice(0, 3);
  }, [events]);

  return (
    <section className="w-full py-20 bg-white flex flex-col justify-center items-center">
      <motion.h2
        className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 text-slate-800 tracking-wide text-center mb-8"
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        viewport={{ once: true }}
      >
        Three Epic Locations
      </motion.h2>

      <motion.p
        className="text-lg font-normal text-slate-800 max-w-[800px] mx-auto px-4 text-center mb-12"
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
        viewport={{ once: true }}
      >
        Discover Indonesia's most spectacular mountain running destinations
        across Java, Bali, and Lombok. Each island offers a unique blend of
        volcanic trails, lush forests, and breathtaking cultural landscapes that
        challenge and inspire every mountain runner.
      </motion.p>

      <div className="container mx-auto px-4 w-full">
        <div className="max-w-7xl mx-auto">
          {error ? (
            <p className="text-red-600 text-center mb-4">{error}</p>
          ) : null}

          <div className="grid md:grid-cols-3 gap-4 items-start">
            {sortedActivities.map((event, index) => (
              <EventCard key={event.id} event={event} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});
